import React, { useCallback } from 'react';
import { observer } from 'mobx-react';
import { runInAction } from 'mobx';

// components
import { Select } from 'components/spaceKit';

// utils
import {
    ColorTagConditionDimension,
    ColorTagConditionMetric,
    getName,
} from '@sprinklr/display-builder/widgets/utils/getColorTag';
import { getMetricDesignOptionsKey } from '../metricDesignTunables/utils/getMetricDesignOptionsKey';

// types
import { AnalyticsProjection } from '@sprinklr/stories/analytics/AnalyticsRequest';
import { FieldSelectionProps } from './types';
import { DATE_FILTER_DATA_TYPES } from '../../../Panel/Picker/PickerSLADateFilter/constants';

function FieldSelection({ widget, colorTag }: FieldSelectionProps) {
    const handleChange = useCallback(
        selection => {
            if (selection) {
                const isMetric = 'measurementName' in selection;

                runInAction(() => {
                    // switching from metric to dimension and vice versa
                    if (isMetric && colorTag.type === 'DIMENSION') {
                        ((colorTag as unknown) as ColorTagConditionMetric).comparison = '>';
                    } else if ('dimensionName' in selection && colorTag.type === 'METRIC') {
                        ((colorTag as unknown) as ColorTagConditionDimension).comparison = 'IN';
                        ((colorTag as unknown) as ColorTagConditionDimension).value = [];
                    }

                    colorTag.type = isMetric ? 'METRIC' : 'DIMENSION';
                    colorTag.name = isMetric ? selection.id : selection.label;
                });
            }
        },
        [colorTag]
    );

    const options = [
        ...widget.analyticsRequests[0].groupBys.filter(
            // filter out date types for now
            groupBy => !DATE_FILTER_DATA_TYPES.includes(groupBy.groupType as any)
        ),
        ...widget.analyticsRequests[0].projections,
    ].map(groupByOrProjection => {
        return {
            id:
                'measurementName' in groupByOrProjection
                    ? getMetricDesignOptionsKey(
                          (groupByOrProjection as AnalyticsProjection).measurementName,
                          (groupByOrProjection as AnalyticsProjection).aggregateFunction
                      )
                    : groupByOrProjection.resolveName,
            label: getName(groupByOrProjection),
            ...groupByOrProjection,
        };
    });

    const value = options.find(option => {
        const optionId = 'measurementName' in option ? option.id : getName(option);
        return optionId === colorTag.name;
    });

    return <Select value={value} options={options} onChange={handleChange} />;
}

const FieldSelectionHOC = observer(FieldSelection);
export { FieldSelectionHOC as FieldSelection };
